import { motion } from "framer-motion";
import { MapPin, Phone, Navigation, Clock } from "lucide-react";
import { BUSINESS, HOURS, dayHours } from "@/data/content";
import { useOpenStatus } from "@/components/site/useOpenStatus";

const MAP_SRC =
  "https://www.google.com/maps?q=Via+Don+Giovanni+Zanardo+26,+31040+Chiarano+TV&output=embed";

export default function Location() {
  const status = useOpenStatus();
  return (
    <section
      id="dove-siamo"
      className="bg-[#FDFBF7] py-16 sm:py-20"
      data-testid="location-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-body text-xs tracking-[0.28em] uppercase text-[#8C9B86] mb-4">
              Dove siamo
            </p>
            <h2 className="font-display font-light tracking-tight leading-[0.95] text-[clamp(2.5rem,6vw,5rem)] text-[#2A1B14]">
              Passa a <span className="italic">trovarci</span>
            </h2>

            <div
              className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-[#E8E3D9] bg-[#F5F2EB] px-4 py-2"
              data-testid="location-status"
            >
              <span className="relative flex h-2.5 w-2.5">
                {status.open && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8C9B86] opacity-75" />
                )}
                <span
                  className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                    status.open ? "bg-[#8C9B86]" : "bg-[#D4AF37]"
                  }`}
                />
              </span>
              <span className="font-body text-sm font-semibold text-[#2A1B14]">
                {status.label}
              </span>
            </div>

            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4">
                <MapPin className="text-[#D4AF37] mt-0.5 shrink-0" size={20} />
                <div className="font-body text-[#2A1B14]">
                  <div className="font-medium">{BUSINESS.address}</div>
                  <div className="text-[#5C4F4A]">{BUSINESS.city}</div>
                </div>
              </div>
              <a
                href={BUSINESS.phoneHref}
                className="flex items-center gap-4 group"
                data-testid="location-phone"
              >
                <Phone className="text-[#D4AF37] shrink-0" size={20} />
                <span className="font-body text-[#2A1B14] font-medium link-underline">
                  {BUSINESS.phone}
                </span>
              </a>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
              <a
                href={BUSINESS.mapsDir}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2A1B14] text-[#FDFBF7] px-5 py-3.5 font-body font-semibold text-sm hover:bg-[#3a271d] transition-all duration-300 hover:scale-[0.98]"
                data-testid="directions-btn"
              >
                <Navigation size={16} /> Indicazioni
              </a>
              <a
                href={BUSINESS.phoneHref}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2A1B14]/20 text-[#2A1B14] px-5 py-3.5 font-body font-semibold text-sm hover:bg-[#F5F2EB] transition-all duration-300"
                data-testid="call-btn"
              >
                <Phone size={16} /> Chiama
              </a>
            </div>

            <div className="mt-10 rounded-xl border border-[#E8E3D9] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 bg-[#F5F2EB] border-b border-[#E8E3D9]">
                <Clock size={16} className="text-[#8C9B86]" />
                <span className="font-body text-sm font-semibold text-[#2A1B14]">
                  Orari di apertura
                </span>
              </div>
              <ul data-testid="hours-list">
                {HOURS.map((h) => {
                  const isToday = h.short === status.today.short;
                  return (
                    <li
                      key={h.day}
                      className={`flex items-center justify-between px-5 py-2.5 font-body text-sm border-b border-[#E8E3D9]/60 last:border-0 ${
                        isToday ? "bg-[#8C9B86]/10" : ""
                      }`}
                    >
                      <span className={`text-[#2A1B14] ${isToday ? "font-semibold" : ""}`}>
                        {h.day}
                      </span>
                      <span className={`${h.slots.length ? "text-[#5C4F4A]" : "text-[#D4AF37]"}`}>
                        {dayHours(h)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl overflow-hidden border border-[#E8E3D9] h-[400px] lg:h-[640px]"
          >
            <iframe
              title="Mappa di Laurie a Chiarano"
              src={MAP_SRC}
              className="w-full h-full grayscale-[0.2]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
